/**
 * hmCopy.js — Central English source-of-truth for every user-visible
 * string across the HustleMeet sales surface. Consumed by the three
 * sales pages (`/meet`, `/circles`,
 * `/matchmaker`) AND by the bulk translation pipeline
 * (`_translate_hustlemeet.py`) which fans this dict out into 29
 * languages and merges it into `i18n.js` under each language block.
 *
 * How to use in JSX:
 *   const { t } = useTranslation();
 *   ...
 *   <h1>{t("hm.landing.hero_title", { defaultValue: HM_COPY.landing.hero_title })}</h1>
 *
 * Or via the convenience helper:
 *   import { hm } from "@/lib/hmCopy";
 *   <h1>{hm(t, "landing.hero_title")}</h1>
 */

export const HM_COPY = {
  // ── Shared nav labels ─────────────────────────────────────────────
  nav: {
    hustlemeet:  "HustleMeet",
    circles:     "Circles",
    matchmaker:  "Matchmaker",
    verified:    "Verified Members",
    meet:        "Meet",
    overview:    "HustleMeetups",
  },

  // ── / (bare hustlemeetups.com root) — overview page ───────────────
  overview: {
    page_title:          "HustleMeetups — three warm ways to find your people.",
    kicker:              "Part of HustleSuite",
    hero_title:          "Verified People. Real Groups. Real Connections.",
    hero_title_accent:   "Real Connections.",
    hero_sub:            "Because meeting people should start with knowing they're real.",
    hero_short_desc:     "HustleMeetups brings verified people together through real-world and online groups across Australia. Join a group that interests you, see upcoming meetups and choose the events you want to attend. Meetups can happen anywhere — restaurants, cafés, hotels, conference rooms, parks, live music venues or online by video.\n\nFrom professional networking and business groups to dinners, social outings, music, walking groups and shared interests, HustleMeetups makes it easier to meet people and build genuine connections.\n\nEvery HustleMeetups member is verified, helping create a safer, more trusted community.\n\nFind your group. Choose your meetup. Meet real people.",

    tile_meet_kicker:    "HustleMeet",
    tile_meet_title:     "Find your people. Do the thing.",
    tile_meet_body:      "Australia's home for interest-first groups. Runners, coders, investors, walkers, dancers, breakfast clubs, book crews — hundreds of groups, real events every week. Show up once, or every week.",
    tile_meet_cta:       "Explore groups",

    tile_circles_kicker: "Circles",
    tile_circles_title:  "Your crew. A new table every time.",
    tile_circles_body:   "Bring your regular group of mates. We book a new restaurant, plan the trivia, deal the cards. You just show up. The tightest way to keep an existing crew close — without anyone having to organise it.",
    tile_circles_cta:    "Start a Circle",

    tile_matchmaker_kicker: "Matchmaker",
    tile_matchmaker_title:  "AI picks the table. You show up.",
    tile_matchmaker_body:   "Take a 10-minute quiz. Overnight, we seat you with strangers who feel like old friends by dessert. Different table every time.",
    tile_matchmaker_cta:    "Take the quiz",

    trust_kicker:        "Why it works",
    trust_title:         "Trust first. Table second.",
    trust_1_title:       "Verified members",
    trust_1_body:        "Every group starts with a real ID check + reliability score. No throwaway accounts.",
    trust_2_title:       "Real hosts",
    trust_2_body:        "Every group is led by a vetted host who owns the vibe, sets the rhythm, and looks after the room.",
    trust_3_title:       "Friendship first",
    trust_3_body:        "Everything here is designed for real friendships, not dating. Ever.",
    trust_4_title:       "Zero-tolerance moderation",
    trust_4_body:        "One report, one review, one decision. Bad actors are removed the same night.",

    cta_join:            "Join Hustle Membership",
    cta_login:           "Sign in",

    footer_line:         "Part of HustleSuite — Australian owned & hosted.",
  },


  // ── /meet ───────────────────────────────────────────────────
  landing: {
    page_title:          "HustleMeet · Find your people. Do the thing.",
    kicker:              "Australia's interest network",
    hero_title:          "Find your people. Do the thing.",
    hero_title_accent:   "your people",
    hero_sub:            "HustleMeet is Australia's home for interest-first groups. Software devs debugging over lunch. Long-course runners at 6am. Investors meeting monthly. Walking groups, dance crews, breakfast clubs, board-game nights. Pick a group, book the next event, and turn up.",
    cta_explore_circles: "Browse groups",
    cta_try_matchmaker:  "Start your own group",
    trust_verified:      "Verified members",
    trust_venues:        "Vetted event hosts",
    trust_friendship:    "Friendship first — never dating",
    trust_moderation:    "Zero-tolerance moderation",

    subbrand_circles_kicker:      "Circles",
    subbrand_circles_title:       "Already got your crew? Give them a permanent home.",
    subbrand_circles_body:        "Circles is for the group of mates who already meet up — book club, poker night, running crew, work friends. We handle the venue, the reminders, and the roster. You just show up.",
    subbrand_circles_b1:          "Rotating venues, one running roster",
    subbrand_circles_b2:          "Auto-reminders + easy RSVPs",
    subbrand_circles_b3:          "Trivia, cards & themed nights on tap",
    subbrand_circles_cta:         "Start a Circle",

    subbrand_matchmaker_kicker:   "Matchmaker",
    subbrand_matchmaker_title:    "AI picks the table. You show up.",
    subbrand_matchmaker_body:     "One 10-minute quiz — personality, values, availability. Our Matchmaker seats you with strangers who feel like old friends by dessert. Different table every time.",
    subbrand_matchmaker_b1:       "Big-5 personality & values scored",
    subbrand_matchmaker_b2:       "Overnight invites when your fit is 0.70 or higher",
    subbrand_matchmaker_b3:       "Learns from every dinner you have",
    subbrand_matchmaker_cta:      "Take the quiz",

    how_kicker:      "How HustleMeet works",
    how_title:       "Four steps. Find your group. Turn up.",
    how_step1_ttl:   "Pick your interests",
    how_step1_sub:   "Running, coding, investing, cooking, music, walking — tell us what you're into and where you live.",
    how_step2_ttl:   "Browse groups near you",
    how_step2_sub:   "Every group publishes its host, its rhythm and its next event. Read the room before you commit.",
    how_step3_ttl:   "RSVP to the next event",
    how_step3_sub:   "One tap, one calendar invite, one clean reminder the day before. Some events are free, some paid — the price is on the tin.",
    how_step4_ttl:   "Show up & connect",
    how_step4_sub:   "Meet the host, meet the crew, do the thing. Come back next week if it clicked.",

    stats_kicker:  "Australia's new way to meet",
    stats_title:   "The reason people keep coming back.",
    stats_92_sub:  "Feel they clicked with at least one person at the event",
    stats_68_sub:  "RSVP to their next event within a week",
    stats_85_sub:  "Stay in touch with someone from the group",

    why_kicker:     "Why we work",
    why_title:      "Real interests. Real hosts. Real events.",
    why_1_ttl:      "Interest-first, not location-first",
    why_1_body:     "The people in your group actually care about the thing you care about — that's the shortcut to real friendship.",
    why_2_ttl:      "Real event hosts",
    why_2_body:     "Every group has a vetted host who owns the vibe, sets the rhythm, and steps in if something's off. Not a Facebook admin. A real person.",
    why_3_ttl:      "Consistency matters",
    why_3_body:     "New events every week. Something to look forward to. A rhythm that turns strangers into your Tuesday-night crew.",
    why_4_ttl:      "Safe & secure",
    why_4_body:     "Verified members, public venues, zero tolerance for disrespect. Our support team is on call before, during and after every event.",
    why_5_ttl:      "Friendship first",
    why_5_body:     "This isn't dating. It's community. Real interests, real people, no swiping in the middle.",
    why_6_ttl:      "Small enough to matter",
    why_6_body:     "Most groups cap events at 4-15 seats. Big enough to spark real conversations. Small enough that nobody gets lost.",

    cities_kicker:      "Where we run",
    cities_title:       "Groups meeting across Australia & NZ.",
    cities_sub_zero:    "First groups opening across 15+ cities — join early and shape the room.",
    cities_sub_some:    "Groups live across 15+ metros. Something in your city, this week.",
    cities_sub_one:     "{{count}} live group · new events every week · always adding cities.",
    cities_sub_many:    "{{count}} live groups · new events every week · always adding cities.",

    forme_kicker:   "This is for you if…",
    forme_title:    "You want real interests. Real people. Real events.",
    forme_1:        "You're over the swipe-app fatigue and want to actually meet people around what you love doing.",
    forme_2:        "You've moved to a new city and need a warm crew, not another chat group.",
    forme_3:        "You value depth over broadcast — one great group beats fifty follows.",
    forme_4:        "You believe shared interests + a set two hours in real life is the real cheat code.",
    notme_kicker:   "This isn't for you if…",
    notme_title:    "HustleMeet doesn't work for everyone.",
    notme_1:        "You can't commit to showing up — hosts and members are counting on your RSVP.",
    notme_2:        "You're looking for a dating service — friendships come first here.",
    notme_3:        "You want to broadcast without meeting anyone — this only works in person.",
    notme_4:        "You expect endless free perks — some events cost. The price is always upfront.",

    safety_kicker:      "Our safety commitment",
    safety_title:       "Warm, but never careless.",
    safety_1_ttl:       "Public venues",
    safety_1_body:      "Every event runs at a public, staffed venue — a cafe, restaurant, park, gym or studio. Never anyone's home unless every attendee opts in.",
    safety_2_ttl:       "Community standards",
    safety_2_body:      "Zero tolerance for disrespect, harassment or dishonesty. Members who breach are removed immediately.",
    safety_3_ttl:       "Privacy protected",
    safety_3_body:      "Your details are never shared publicly. You control who you stay connected with after each event.",
    safety_4_ttl:       "Support on call",
    safety_4_body:      "Our team is available before, during and after every event if you have questions or concerns.",

    bottom_title:       "Your next favourite group is one tap away.",
    bottom_sub:         "Pick your path — join an existing group, bring your crew into a Circle, or let the Matchmaker seat you at a table that already fits.",
    bottom_cta_circles: "Start a Circle",
    bottom_cta_mm:      "Take the Matchmaker quiz",
  },

  // ── /circles ───────────────────────────────────────────
  circles: {
    page_title:          "HustleMeet Circles · Your crew, new venue every time",
    kicker:              "HustleMeet · Circles",
    hero_title:          "Your crew. A new table every time.",
    hero_title_accent:   "Your crew",
    hero_sub:            "Already got your mates? Circles is the easiest way to keep them close. We book the venue, plan the trivia, deal the cards. You just turn up — same crew, new night, new place.",
    cta_find:            "Start a Circle",
    cta_host_hero:       "Join an open Circle",
    cta_join:            "Join Hustle Membership",
    trust_venue:         "Rotating vetted venues",
    trust_party:         "4-12 mates per Circle",
    trust_verified:      "Verified members",
    trust_friendship:    "Real friendships, kept close",

    how_kicker:      "How it works",
    how_title:       "Four steps. Your crew stays a crew.",
    how_step1_ttl:   "Start your Circle",
    how_step1_sub:   "Name it, invite your mates (up to 12), and pick a rhythm — weekly, fortnightly or monthly.",
    how_step2_ttl:   "We pick the venue",
    how_step2_sub:   "Every event we suggest a new spot — restaurant, trivia night, card room, wine bar. Or lock in a favourite.",
    how_step3_ttl:   "Everyone RSVPs in one tap",
    how_step3_sub:   "Auto-reminders, cost split, prepaid seats so no-shows don't wreck the plan. The bill is done before the entrée lands.",
    how_step4_ttl:   "Keep the rhythm",
    how_step4_sub:   "One night in the diary that never has to be organised again. The group tightens over months, not minutes.",

    cadence_weekly_ttl:      "Weekly",
    cadence_weekly_sub:      "Every week",
    cadence_weekly_body:     "For crews who want a Tuesday-night ritual — same faces, new place every time.",
    cadence_fortnight_ttl:   "Fortnightly",
    cadence_fortnight_sub:   "Every 2 weeks",
    cadence_fortnight_body:  "The sweet spot — regular without being demanding on busy diaries.",
    cadence_monthly_ttl:     "Monthly",
    cadence_monthly_sub:     "Every month",
    cadence_monthly_body:    "Special-occasion Circles — Sunday roasts, big trivia nights, degustation, poker.",

    feast_kicker:            "Pick your night",
    feast_title:             "Three ways to spend it",
    feast_sub:               "Every Circle picks how the night runs. Rotate them, or lock in your crew's favourite.",
    feast_surprise_ttl:      "Try a new restaurant",
    feast_surprise_body:     "We short-list {{count}} venues near you, everyone votes, kitchen preps for a full table. Cost split, prepaid seats.",
    feast_preselect_ttl:     "Trivia / cards / games",
    feast_preselect_body:    "Book a trivia night, a poker room, board-game bar or bowling lane. We handle the venue, the buy-in and the reminders.",
    feast_per_event_ttl:     "One-off themed night",
    feast_per_event_body:    "Wine tasting, chef's table, cocktail class, escape room. Something special once in a while — fully organised.",

    live_kicker:             "Right now",
    live_title:              "Circles you can join",
    live_host_link:          "Start your own",
    live_loading:            "Loading…",
    live_empty_title:        "Be the first Circle in your city.",
    live_empty_sub:          "Start one with three mates and we'll help you invite the rest — plus give you the first three venues on us.",
    live_empty_cta:          "Start a Circle",

    host_kicker:             "For Circle leaders",
    host_title:              "You bring the crew. We do the admin.",
    host_body:               "If you're the mate who always organises the night, Circles saves you hours. Auto-reminders, prepaid RSVPs, venue short-lists, cost splits, waitlist management and a cashback wallet on every night out. You just show up as a normal member.",
    host_perk_1:             "Auto venue short-lists — never scroll Google again",
    host_perk_2:             "Prepaid seats — zero no-shows",
    host_perk_3:             "Cost split done before dessert",
    host_perk_4:             "Reminders + calendar invites on autopilot",
    host_perk_5:             "Waitlist with auto-promote if a mate cancels",
    host_perk_6:             "Cashback wallet on every night out",
    host_cta:                "Start a Circle now",

    why_kicker:              "Why Circles work",
    why_title:               "Adult friendships need infrastructure",
    why_1_ttl:               "Same crew, deeper trust",
    why_1_body:              "Life pulls friends apart. Circles are the fixed rhythm that keeps everyone close — the same 4-12 mates, meeting on the same weeknight, no matter what.",
    why_2_ttl:               "Zero admin fatigue",
    why_2_body:              "Nobody has to be the group WhatsApp chief. We pick the venues, send the reminders, take the payments, close the loop.",
    why_3_ttl:               "New venue, new memory",
    why_3_body:              "Rotate through Australia's best small restaurants, trivia rooms and game bars. Familiar faces, fresh backdrops — the sweet spot of routine and novelty.",

    bottom_title:            "The night your crew looks forward to, without anyone having to plan it.",
    bottom_sub:              "One Circle. Same mates. New venue every time.",
    bottom_cta_find:         "Start a Circle",
    bottom_cta_mm:           "Meet new people with Matchmaker →",

    // Postcode waitlist (email capture)
    waitlist_kicker:         "No Circle yet in your suburb?",
    waitlist_title:          "Be the first to know.",
    waitlist_sub:            "Leave your postcode and email. We will let you know when a Circle opens near you.",
    waitlist_email_ph:       "you@example.com",
    waitlist_postcode_ph:    "Postcode",
    waitlist_name_ph:        "First name (optional)",
    waitlist_cta:            "Notify me",
    waitlist_sending:        "Sending…",
    waitlist_success:        "You are on the list. We will email you when a Circle opens in your postcode.",
    waitlist_success_crowd:  "You're #{{n}} in this postcode. We open Circles as soon as we hit 3.",
    waitlist_err_generic:    "Something went wrong. Please try again in a moment.",

    card_host_pill:          "Circle",
  },

  // ── /matchmaker ────────────────────────────────────────
  matchmaker: {
    page_title:          "HustleMeet Matchmaker · AI picks the right table",
    kicker:              "HustleMeet · Matchmaker",
    hero_title:          "AI picks the table. You just show up.",
    hero_title_accent:   "You just show up.",
    hero_sub:            "Answer a short personality & values quiz. Our Matchmaker seats you with four to seven strangers who will feel like old friends by dessert. No swiping. No small talk. Just a shared table, ready when you are.",
    cta_quiz:            "Take the matching quiz",
    cta_see_circles:     "See Circles instead →",
    cta_join:            "Join Hustle Membership",
    trust_big5:          "Big-5 personality scored",
    trust_seats:         "4-8 seats per table",
    trust_lock:          "Zero-swipe · zero-photo",
    trust_verified:      "Verified members only",

    how_kicker:      "How it works",
    how_title:       "Four steps to a table that already fits.",
    how_step1_ttl:   "Take the quiz",
    how_step1_sub:   "Ten-minute questionnaire — personality, values, dietary, availability. You do it once.",
    how_step2_ttl:   "Overnight matching",
    how_step2_sub:   "Our AI reads your profile and Australia's live dinner slate. When your fit score is 0.70 or higher, it drafts a personal invite.",
    how_step3_ttl:   "Accept or decline",
    how_step3_sub:   "You get one clean invite email with the venue, the table theme, and the three interests you share with the group.",
    how_step4_ttl:   "It learns from you",
    how_step4_sub:   "Every post-dinner survey tunes the model. Your next match gets smarter the more you dine.",

    science_kicker:  "Why it works",
    science_title:   "The Matchmaker doesn't guess — it scores.",
    science_sub:     "Every match is a weighted composite of four factors. When the whole table averages above 0.70, we send the invites. Anything below sits in the queue.",
    science_1_ttl:   "Personality (35%)",
    science_1_body:  "Cosine similarity across the Big-5 personality vector — openness, conscientiousness, extraversion, agreeableness, and emotional stability.",
    science_2_ttl:   "Values (25%)",
    science_2_body:  "Overlap on the values that make a dinner conversation actually land — family, curiosity, growth, service, adventure.",
    science_3_ttl:   "Interests (25%)",
    science_3_body:  "Jaccard overlap on your specific interests — hiking Six Foot Track, Sydney film club, Melbourne coffee scene, your third book of the year.",
    science_4_ttl:   "Availability (15%)",
    science_4_body:  "The night you can actually make. No 7pm Tuesday invite if your calendar says gym-Tuesday-forever.",

    safety_kicker:   "Safety by design",
    safety_title:    "The Matchmaker is quietly cautious.",
    safety_1_ttl:    "Confidence threshold",
    safety_1_body:   "Below a 0.70 group score, no invite goes out. A shaky match beats no dinner — until it doesn't.",
    safety_2_ttl:    "Mutual-no blocklist",
    safety_2_body:   "If two members privately signal 'no' after a dinner, we never seat them together again.",
    safety_3_ttl:    "Auto-kill switch",
    safety_3_body:   "If 20% or more of recent surveys rate a table 2/5 or lower, invites pause for 24h while an operator reviews.",
    safety_4_ttl:    "Community standards",
    safety_4_body:   "Every member accepts a plain-English code of conduct. We remove people who breach it — no appeals.",
    safety_5_ttl:    "Public venues only",
    safety_5_body:   "Every Matchmaker dinner runs at a real restaurant with staff. Never anyone's home.",
    safety_6_ttl:    "Post-event feedback",
    safety_6_body:   "Your private survey shapes future matches, and can trigger a mod review of the venue or a peer.",

    loop_kicker:     "The learning loop",
    loop_title:      "Every dinner makes your next dinner better.",
    loop_body:       "The Matchmaker isn't static. After each event you submit a short private survey — how the conversation felt, who you'd dine with again, whether the venue delivered. Those signals feed back into the model and reshape every future match.",
    loop_1:          "Peer \"dine again\" votes strengthen your pairings",
    loop_2:          "Mutual-no votes silently retire pairs",
    loop_3:          "Venue & conversation ratings tune the seat mix",
    loop_4:          "Reliability score protects members from repeat no-shows",
    loop_5:          "Learning bias reinforces good matches month over month",
    loop_6:          "You stay in control — opt-out or reset your profile anytime",

    vs_kicker:       "Which is right for me?",
    vs_title:        "Matchmaker or Circles?",
    vs_mm_ttl:       "Pick me if I want variety.",
    vs_mm_1:         "A different table, city, cuisine every time",
    vs_mm_2:         "AI-curated group of 4-8 compatible strangers",
    vs_mm_3:         "Weekly rhythm, no host commitment",
    vs_mm_4:         "Great if you're new to a city or travelling",
    vs_mm_cta:       "Take the matching quiz",
    vs_circles_ttl:  "Pick me if I want a regular.",
    vs_circles_1:    "Same host · same venue · same weeknight",
    vs_circles_2:    "A group that turns into your Wednesday tradition",
    vs_circles_3:    "Human-curated by an experienced host",
    vs_circles_4:    "Great if you've just moved and want roots",
    vs_circles_cta:  "Browse Circles",

    bottom_title:    "One quiz. Every dinner after that already fits.",
    bottom_sub:      "You'll get matched inside a fortnight. It's the easiest \"yes\" you'll make this month.",
    bottom_cta:      "Take the matching quiz",
    bottom_note:     "No credit card · 10 minutes · Unmatch anytime",
  },
};

/**
 * Convenience helper — `hm(t, "landing.hero_title")` returns the
 * translated string with the English default embedded so t() always
 * has a fallback. Accepts an optional interpolation dict.
 */
export function hm(t, key, opts) {
  const path = key.split(".");
  let def = HM_COPY;
  for (const p of path) def = def?.[p];
  if (typeof def !== "string") def = key;   // safe fallback
  return t(`hm.${key}`, { defaultValue: def, ...(opts || {}) });
}

export default HM_COPY;
